/**
 * Centralized Shift Status Calculation Service
 * Single source of truth for all shift status calculation logic
 */

import { parseISO, isPast, isAfter, isBefore, differenceInMinutes, addMinutes, addHours } from 'date-fns';
import {
  ShiftStatus,
  ShiftWithEmployee,
  ShiftStatusBusinessRules,
  DEFAULT_BUSINESS_RULES
} from './shift-status-types';
import { convertUtcDateToBusinessTimezone, createBusinessTimezoneDate } from '../core/timezone-utils';

/**
 * Result interface with timing precision
 */
export interface ShiftStatusResult {
  status: ShiftStatus;
  lateMinutes?: number;
  minutesEarly?: number;
  minutesLeft?: number;
  overtimeMinutes?: number;
}

/**
 * Enhanced shift status calculation service with timing precision
 */
export class ShiftStatusService {
  private businessRules: ShiftStatusBusinessRules;
  private businessTimezone: string;

  constructor(businessRules: Partial<ShiftStatusBusinessRules> = {}, businessTimezone: string = 'UTC') {
    this.businessRules = { ...DEFAULT_BUSINESS_RULES, ...businessRules };
    this.businessTimezone = businessTimezone;
  }

  /**
   * Calculate comprehensive shift status based on all available data
   * Optimized to use stored status for historical shifts when available
   */
  calculateShiftStatus(
    shiftStartTime: string | Date,
    shiftEndTime: string | Date,
    employeeId: string | null,
    arrivalTime?: string | Date | null,
    departureTime?: string | Date | null,
    currentTime?: Date,
    storedStatus?: ShiftStatus | null,
    isHistorical?: boolean
  ): ShiftStatus {
    // Optimization: Use stored status for historical shifts to avoid expensive calculations
    if (isHistorical && storedStatus) {
      return storedStatus;
    }

    // Get current time in business timezone if not provided
    const businessCurrentTime = currentTime || this.getCurrentTimeInBusinessTimezone();

    return this.calculateDetailedShiftStatus(
      shiftStartTime,
      shiftEndTime,
      employeeId,
      arrivalTime,
      departureTime,
      businessCurrentTime
    ).status;
  }

  /**
   * Calculate detailed shift status with timing precision
   * Optimized to use stored status for historical shifts when available
   */
  calculateDetailedShiftStatus(
    shiftStartTime: string | Date,
    shiftEndTime: string | Date,
    employeeId: string | null,
    arrivalTime?: string | Date | null,
    departureTime?: string | Date | null,
    currentTime?: Date,
    storedStatus?: ShiftStatus | null,
    isHistorical?: boolean
  ): ShiftStatusResult {
    // Optimization: Use stored status for historical shifts to avoid expensive calculations
    if (isHistorical && storedStatus) {
      return { status: storedStatus };
    }
    // Get current time in business timezone if not provided
    const businessCurrentTime = currentTime || this.getCurrentTimeInBusinessTimezone();

    // Parse dates safely with proper timezone handling
    const startTime = this.parseTimestampWithTimezone(shiftStartTime);
    const endTime = this.parseTimestampWithTimezone(shiftEndTime);
    const arrival = arrivalTime ? this.parseTimestampWithTimezone(arrivalTime) : null;
    const departure = departureTime ? this.parseTimestampWithTimezone(departureTime) : null;

    // Validate dates
    if (!this.isValidDate(startTime) || !this.isValidDate(endTime)) {
      console.warn('Invalid shift times provided to calculateDetailedShiftStatus');
      return { status: ShiftStatus.PENDING };
    }

    // Handle unassigned shifts
    if (!employeeId) {
      return {
        status: ShiftStatus.UNASSIGNED
      };
    }

    // Calculate status based on time progression and employee actions
    return this.determineDetailedStatusFromContext(startTime, endTime, arrival, departure, businessCurrentTime);
  }

  /**
   * Update business rules configuration
   */
  updateBusinessRules(newRules: Partial<ShiftStatusBusinessRules>): void {
    this.businessRules = { ...this.businessRules, ...newRules };
  }

  /**
   * Get current business rules
   */
  getBusinessRules(): ShiftStatusBusinessRules {
    return { ...this.businessRules };
  }

  // Private helper methods

  /**
   * Get current time in business timezone
   */
  private getCurrentTimeInBusinessTimezone(): Date {
    const utcNow = new Date();
    return convertUtcDateToBusinessTimezone(utcNow, this.businessTimezone);
  }

  /**
   * Parse a timestamp with proper timezone handling
   * Assumes input is UTC and converts to business timezone
   */
  private parseTimestampWithTimezone(timestamp: string | Date): Date {
    if (timestamp instanceof Date) {
      // Already a Date object, convert from UTC to business timezone
      return convertUtcDateToBusinessTimezone(timestamp, this.businessTimezone);
    }

    try {
      // Parse as UTC timestamp and convert to business timezone
      const utcDate = new Date(timestamp);
      if (!this.isValidDate(utcDate)) {
        console.warn(`Invalid timestamp provided: ${timestamp}`);
        return new Date(); // Fallback to current time
      }
      return convertUtcDateToBusinessTimezone(utcDate, this.businessTimezone);
    } catch (error) {
      console.warn(`Failed to parse timestamp ${timestamp}:`, error);
      return new Date(); // Fallback to current time
    }
  }

  private determineDetailedStatusFromContext(
    startTime: Date,
    endTime: Date,
    arrival: Date | null,
    departure: Date | null,
    currentTime: Date
  ): ShiftStatusResult {
    const gracePeriodEnd = addMinutes(startTime, this.businessRules.gracePeriodMinutes);
    const missedThreshold = addMinutes(startTime, this.businessRules.autoMissedAfterMinutes);

    // Future shift
    if (isBefore(currentTime, startTime)) {
      return {
        status: ShiftStatus.PENDING
      };
    }

    // Current shift (between start and end time)
    if (isAfter(currentTime, startTime) && isBefore(currentTime, endTime)) {
      if (!arrival) {
        return { status: ShiftStatus.MISSED };
      }

      // Check if employee has already departed (manual departure time set)
      if (departure && isAfter(currentTime, departure)) {
        // Employee has left - determine completion status based on departure timing
        const earlyDepartureThreshold = addMinutes(endTime, -this.businessRules.earlyDepartureThresholdMinutes);
        if (isBefore(departure, earlyDepartureThreshold)) {
          return { status: ShiftStatus.LEFT_EARLY };
        }
        
        // Determine completion quality based on arrival timing
        const minutesEarly = differenceInMinutes(startTime, arrival);
        const lateMinutes = differenceInMinutes(arrival, startTime);

        if (minutesEarly >= 30) {
          return { 
            status: ShiftStatus.TOO_EARLY, 
            minutesEarly 
          };
        } else if (isAfter(arrival, startTime)) {
          const lateThreshold = addMinutes(startTime, this.businessRules.lateArrivalThresholdMinutes);
          const wasLate = isAfter(arrival, lateThreshold);
          return { 
            status: wasLate ? ShiftStatus.COMPLETED_LATE : ShiftStatus.COMPLETED,
            lateMinutes: wasLate ? lateMinutes : undefined
          };
        } else {
          return { status: ShiftStatus.COMPLETED };
        }
      }

      // Employee is still present - check arrival timing for current status
      const minutesEarly = differenceInMinutes(startTime, arrival);
      const lateMinutes = differenceInMinutes(arrival, startTime);

      if (minutesEarly >= 30) {
        return { 
          status: ShiftStatus.TOO_EARLY, 
          minutesEarly 
        };
      } else if (isAfter(arrival, startTime)) {
        return { 
          status: ShiftStatus.LATE, 
          lateMinutes 
        };
      } else {
        // On-time arrival - shift is now in progress
        return { status: ShiftStatus.IN_PROGRESS };
      }
    }

    // Overtime detection - employee is working past scheduled end time
    if (isAfter(currentTime, endTime) && this.businessRules.enableOvertimeTracking) {
      if (arrival && !departure) {
        // Employee has arrived but not departed - working overtime
        const overtimeThreshold = addMinutes(endTime, this.businessRules.overtimeThresholdMinutes);
        const overtimeTimeoutThreshold = addHours(endTime, this.businessRules.overtimeTimeoutHours);

        if (isAfter(currentTime, overtimeTimeoutThreshold)) {
          // Overtime has exceeded timeout threshold - transition to no departure scan
          return {
            status: ShiftStatus.NO_DEPARTURE_SCAN
          };
        } else if (isAfter(currentTime, overtimeThreshold)) {
          // Working overtime but within timeout threshold
          const overtimeMinutes = differenceInMinutes(currentTime, endTime);

          // Check for composite status (arrival timing + overtime)
          const minutesEarly = differenceInMinutes(startTime, arrival);
          const lateMinutes = differenceInMinutes(arrival, startTime);
          const lateThreshold = addMinutes(startTime, this.businessRules.lateArrivalThresholdMinutes);

          if (minutesEarly >= this.businessRules.earlyArrivalThresholdMinutes) {
            // Arrived early AND working overtime
            return {
              status: ShiftStatus.TOO_EARLY_OVERTIME,
              minutesEarly,
              overtimeMinutes
            };
          } else if (isAfter(arrival, lateThreshold)) {
            // Arrived late AND working overtime
            return {
              status: ShiftStatus.LATE_OVERTIME,
              lateMinutes,
              overtimeMinutes
            };
          }

          return {
            status: ShiftStatus.OVERTIME,
            overtimeMinutes
          };
        }
      }
    }

    // Past shift - determine completion status
    if (isAfter(currentTime, endTime)) {
      if (!arrival) {
        // No arrival recorded - shift is missed
        return { status: ShiftStatus.MISSED };
      }

      // Calculate arrival timing metrics upfront for composite status checks
      const arrivalMinutesEarly = differenceInMinutes(startTime, arrival);
      const arrivalLateMinutes = differenceInMinutes(arrival, startTime);
      const lateThreshold = addMinutes(startTime, this.businessRules.lateArrivalThresholdMinutes);
      const wasArrivedEarly = arrivalMinutesEarly >= this.businessRules.earlyArrivalThresholdMinutes;
      const wasArrivedLate = isAfter(arrival, lateThreshold);

      // Check for early departure using business rule threshold
      if (departure) {
        const earlyDepartureThreshold = addMinutes(endTime, -this.businessRules.earlyDepartureThresholdMinutes);
        if (isBefore(departure, earlyDepartureThreshold)) {
          // Check for composite status (arrival timing + left early)
          if (wasArrivedEarly) {
            return {
              status: ShiftStatus.TOO_EARLY_LEFT_EARLY,
              minutesEarly: arrivalMinutesEarly
            };
          } else if (wasArrivedLate) {
            return {
              status: ShiftStatus.LATE_LEFT_EARLY,
              lateMinutes: arrivalLateMinutes
            };
          }
          return { status: ShiftStatus.LEFT_EARLY };
        }
      }

      // Check for overtime completion (departed after scheduled end time)
      if (departure && isAfter(departure, endTime) && this.businessRules.enableOvertimeTracking) {
        const overtimeThreshold = addMinutes(endTime, this.businessRules.overtimeThresholdMinutes);
        if (isAfter(departure, overtimeThreshold)) {
          const overtimeMinutes = differenceInMinutes(departure, endTime);
          // Check for composite status (arrival timing + overtime)
          if (wasArrivedEarly) {
            return {
              status: ShiftStatus.TOO_EARLY_OVERTIME,
              minutesEarly: arrivalMinutesEarly,
              overtimeMinutes
            };
          } else if (wasArrivedLate) {
            return {
              status: ShiftStatus.LATE_OVERTIME,
              lateMinutes: arrivalLateMinutes,
              overtimeMinutes
            };
          }
          return {
            status: ShiftStatus.OVERTIME,
            overtimeMinutes
          };
        }
      }

      // Arrival recorded - determine completion quality (reuse pre-calculated values)
      if (wasArrivedEarly) {
        return {
          status: ShiftStatus.TOO_EARLY,
          minutesEarly: arrivalMinutesEarly
        };
      } else if (wasArrivedLate) {
        return {
          status: ShiftStatus.COMPLETED_LATE,
          lateMinutes: arrivalLateMinutes
        };
      } else if (isAfter(arrival, startTime)) {
        // Arrived late but within grace period
        return {
          status: ShiftStatus.COMPLETED,
          lateMinutes: arrivalLateMinutes
        };
      } else {
        return {
          status: ShiftStatus.ON_TIME
        };
      }
    }

    return { status: ShiftStatus.PENDING };
  }

  private isValidDate(date: unknown): date is Date {
    return date instanceof Date && !isNaN(date.getTime());
  }
}

// Export factory function for custom configurations
export const createShiftStatusService = (
  rules?: Partial<ShiftStatusBusinessRules>,
  businessTimezone?: string
): ShiftStatusService => {
  return new ShiftStatusService(rules, businessTimezone);
};

